// skipcq: JS-W1028
import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { listBeerIngestionRunsForBrewery } from '../services/breweryBeerSourcesService';
import { db } from '../utils/firebase';
import { resolveUserWelcomeName } from '../utils/user/resolveUserWelcomeName';

async function loadUserNamesById(userIds) {
  const uniqueIds = [
    ...new Set(
      (userIds || [])
        .map((userId) => (typeof userId === 'string' ? userId.trim() : ''))
        .filter((userId) => userId.length > 0),
    ),
  ];

  const entries = await Promise.all(
    uniqueIds.map(async (userId) => {
      try {
        const userSnap = await getDoc(doc(db, 'users', userId));
        const userData = userSnap.exists() ? userSnap.data() : {};
        const resolvedName = resolveUserWelcomeName(userData);
        return [userId, resolvedName || userId];
      } catch {
        return [userId, userId];
      }
    }),
  );

  return Object.fromEntries(entries);
}

export default function useBeerIngestionRuns(breweryId) {
  const [runs, setRuns] = useState([]);
  const [userNamesById, setUserNamesById] = useState({});
  const [loading, setLoading] = useState(Boolean(breweryId));
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!breweryId) {
      return undefined;
    }

    let cancelled = false;

    const loadRuns = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listBeerIngestionRunsForBrewery(breweryId);
        if (cancelled) {
          return;
        }
        setRuns(rows);
        const names = await loadUserNamesById(rows.map((run) => run.userId));
        if (!cancelled) {
          setUserNamesById(names);
        }
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        setError(loadError?.message || 'Failed to load ingestion runs');
        setRuns([]);
        setUserNamesById({});
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRuns();
    return () => {
      cancelled = true;
    };
  }, [breweryId, reloadToken]);

  if (!breweryId) {
    return {
      runs: [],
      userNamesById: {},
      loading: false,
      error: null,
      refresh,
    };
  }

  return {
    runs,
    userNamesById,
    loading,
    error,
    refresh,
  };
}
